import styled from 'styled-components'
import {api} from 'lib/url'
import {useOrganization} from 'organization/OrganizationProvider'
import React, {useCallback, useEffect, useState} from 'react'
import GridCard from 'organization/EventList/Cards/GridCard'
import ListCard from 'organization/EventList/Cards/ListCard'
import Page from 'lib/ui/layout/Page'
import {ObvioEvent} from 'Event'
import Layout from 'organization/user/Layout'
import {useBreadcrumbs} from 'lib/ui/BreadcrumbProvider'
import {usePageView} from 'analytics'
import Title from 'lib/ui/PageHeader/Title'
import {CREATE_EVENTS} from 'organization/PermissionsProvider'
import HasPermission from 'organization/HasPermission'
import NewItemButton from 'lib/ui/PageHeader/NewItemButton'
import {useIsOwner} from 'organization/OwnerProvider'
import {useToggleArray} from 'lib/toggle'
import Button from 'lib/ui/Button'
import {
  ToggleViewIcon,
  DesktopPageHeader,
  MobilePageHeader,
  HeaderLink,
} from 'lib/ui/GridCollection'
import Icon from 'lib/ui/Icon'
import {useInterval} from 'lib/interval'
import useDebounce from 'lib/debounce'
import TextField from '@material-ui/core/TextField'
import {onChangeStringHandler} from 'lib/dom'

/**
 * The events list doesn't need all of the Event's attributes, or
 * relations, so we'll just return the minimum required here to
 * speed up load times.
 */
export type ListEventData = Pick<
  ObvioEvent,
  | 'id'
  | 'end'
  | 'logo'
  | 'name'
  | 'num_expected_attendees'
  | 'slug'
  | 'start'
  | 'timezone'
  | 'system_domain_id'
  | 'is_copying'
  | 'is_deleting'
  | 'is_online'
  | 'is_live'
>

export default function EventList() {
  const {organization, routes, client} = useOrganization()
  const isOwner = useIsOwner()
  const [showingListView, toggleListView] = useToggleArray()
  const [showSearchBox, toggleSearchBoxVisibility] = useToggleArray()

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 400)
  const [loading, setLoading] = useState(true)
  const [events, setEvents] = useState<ListEventData[] | null>(null)

  const hasEventBeingCopied = events?.some((event) => event.is_copying)

  usePageView()

  const canAccessSettings = isOwner
  const homeLink = canAccessSettings ? routes.settings : routes.events.root

  useBreadcrumbs([
    {title: organization.name, url: homeLink},
    {title: 'Events', url: routes.events.root},
  ])

  const fetchEvents = useCallback(() => {
    const trimmedSearchQuery = debouncedSearchQuery.trim()
    const params = new URLSearchParams()
    if (trimmedSearchQuery) {
      params.set('search', trimmedSearchQuery)
    }

    const queryString = params.toString()
    const basePath = `/organizations/${organization.id}/events`
    const url = api(queryString ? `${basePath}?${queryString}` : basePath)

    client
      .get<ListEventData[]>(url)
      .then((events: ListEventData[]) => {
        setEvents(events)
      })
      .finally(() => setLoading(false))
  }, [client, organization.id, debouncedSearchQuery])

  const onDelete = (target: {id: number}) => {
    setEvents((events) => {
      if (!events) {
        return null
      }

      return events.map((event) =>
        event.id === target.id ? {...event, is_deleting: true} : event,
      )
    })
  }

  const toggleSearchBox = () => {
    if (showSearchBox) {
      setSearchQuery('')
    }

    toggleSearchBoxVisibility()
  }

  useInterval(fetchEvents, 3000, hasEventBeingCopied === false)
  useEffect(fetchEvents, [fetchEvents])

  if (loading || !events) {
    return null
  }

  return (
    <Layout>
      <Page>
        <DesktopPageHeader>
          <Title text="Events" />
          <Container>
            <Button onClick={toggleSearchBox} aria-label="toggle search">
              <SearchIcon
                className="fa-solid fa-magnifying-glass"
                iconSize={20}
              />
            </Button>
            <Button onClick={toggleListView} aria-label="toggle view">
              <ToggleViewIcon isListView={showingListView} />
            </Button>
            <HasPermission required={CREATE_EVENTS}>
              <HeaderLink disableStyles to={routes.events.create}>
                <StyledNewItemButton
                  text="New Event"
                  aria-label="create event"
                />
              </HeaderLink>
            </HasPermission>
          </Container>
        </DesktopPageHeader>
        <MobilePageHeader>
          <Title text="Events" />
          <HasPermission required={CREATE_EVENTS}>
            <HeaderLink disableStyles to={routes.events.create}>
              <StyledNewItemButton text="New Event" aria-label="create event" />
            </HeaderLink>
          </HasPermission>
          <Container>
            <Button onClick={toggleSearchBox} aria-label="toggle search">
              <SearchIcon
                className="fa-solid fa-magnifying-glass"
                iconSize={20}
              />
            </Button>
            <Button onClick={toggleListView} aria-label="toggle view">
              <ToggleViewIcon isListView={showingListView} />
            </Button>
          </Container>
        </MobilePageHeader>
        <SearchBox
          isVisible={showSearchBox}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
        <Content
          events={events}
          searchQuery={searchQuery}
          showingListView={showingListView}
          onDelete={onDelete}
        />
      </Page>
    </Layout>
  )
}

function SearchBox(props: {
  isVisible: boolean
  searchQuery: string
  setSearchQuery: (query: string) => void
}) {
  const {isVisible, searchQuery, setSearchQuery} = props
  if (!isVisible) {
    return null
  }

  return (
    <SearchBoxContainer>
      <TextField
        variant="outlined"
        label="Search"
        value={searchQuery}
        onChange={onChangeStringHandler(setSearchQuery)}
        fullWidth
        inputProps={{
          'aria-label': 'search for event',
        }}
      />
    </SearchBoxContainer>
  )
}

function Content(props: {
  events: ListEventData[]
  searchQuery: string
  showingListView: boolean
  onDelete: (event: {id: number}) => void
}) {
  const {events, searchQuery, showingListView, onDelete} = props
  const isEmpty = events.length === 0
  if (isEmpty) {
    if (searchQuery !== '') {
      return <EmptySearchResultsMessage />
    }

    return <EmptyCreatedEventsMessage />
  }

  if (showingListView) {
    return (
      <>
        {events.map((e, idx) => (
          <ListCard key={e.id} event={e} index={idx} onDelete={onDelete} />
        ))}
      </>
    )
  }

  return (
    <Grid>
      {events.map((e) => (
        <GridCard key={e.id} event={e} onDelete={onDelete} />
      ))}
    </Grid>
  )
}

function EmptySearchResultsMessage() {
  return (
    <EmptyBox>
      <p>No events match your search</p>
    </EmptyBox>
  )
}

function EmptyCreatedEventsMessage() {
  return (
    <EmptyBox>
      <p>No events have been created</p>
    </EmptyBox>
  )
}

const column = `minmax(270px, auto)`

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto;
  grid-gap: ${(props) => props.theme.spacing[8]};

  @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
    grid-template-columns: ${column} ${column};
    grid-template-rows: auto auto;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    grid-template-columns: ${column} ${column} ${column};
    grid-template-rows: auto auto auto;
  }

  @media (min-width: ${(props) => props.theme.breakpoints.lg}) {
    grid-template-columns: ${column} ${column} ${column} ${column};
    grid-template-rows: auto auto auto auto;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`

const StyledNewItemButton = styled(NewItemButton)`
  color: #ffffff !important;
`

const SearchBoxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${(props) => props.theme.spacing[6]};
`

const EmptyBox = styled.div`
  margin-top: ${(props) => props.theme.spacing[8]};
  text-align: center;
`

const SearchIcon = styled(Icon)`
  color: ${(props) => props.theme.colors.gray200};
`
